import {useEffect, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import useSoopiAnimation from '@/components/soopi/hooks/useSoopiAnimation';
import useSoopiHandler from '@/components/soopi/hooks/useSoopiHandler';
import useSoopiManager from '@/components/soopi/hooks/useSoopiManager';
import useSearchInpFocus from '@/components/soopi/hooks/useSearchInpFocus';
import useFloatingSize from '@/components/soopi/hooks/useFloatingSize';
import SoopiIframeContainer from '@/components/soopi/SoopiIframeContainer';
import SoopiResizeScrollButton from '@/components/soopi/SoopiResizeScrollButton';
import SoopiBorder from '@/components/soopi/SoopiBorder';
import InteractivePlayVideo from '@/components/soopi/InteractivePlayVideo';
import styles from './styles.module.css';
import {FloatingPortal} from '@/components/main/common';
import {ROUTE} from '@/constants/main/route';
import {throwDialog} from '@/utils/ui/dialog';
import useInteraction from './hooks/useInteraction';
import LogApi from '@/api/common/log';

const SoopiComponent = () => {
  const [soopiTicket, setSoopiTicket] = useState({});
  const updateSoopiTicket = (soopiTicket) => {
    setSoopiTicket(soopiTicket);
  };

  const [isResizing, setIsResizing] = useState(false);
  const [height, setHeight] = useState('750');

  const {isLeft, isDragging, handleDragStart, handleDragEnd, position} =
    useSoopiAnimation();

  const {
    iframeUrl,
    iframeVisible,
    handleOpenSoopi,
    closeIframe,
    streamerProfile,
    previewBroadInfo,
    dragPointStyle,
    previewOpen,
    handlePreviewClose,
    handleLandingClose,
  } = useSoopiHandler(soopiTicket, isDragging, updateSoopiTicket, setHeight);

  useSearchInpFocus(iframeVisible);
  useInteraction(iframeVisible);

  const {isDisplay, handleCloseSoopiBtn} = useSoopiManager(
    iframeVisible,
    closeIframe,
    updateSoopiTicket,
    isDragging,
    handleLandingClose,
  );

  const openSoopPreviewModal = async (
    streamerName,
    broadType,
    broadUrl,
    data,
  ) => {
    await throwDialog(
      'soopi/SoopiPreviewModal',
      {
        $streamerName: streamerName,
        broadType: broadType,
        broadUrl: broadUrl,
        handlePreviewClose,
        data: data,
      },
      {isCustomDialog: true},
    );
    handlePreviewClose();
  };

  useEffect(() => {
    closeIframe();
  }, [location.pathname]);

  useEffect(() => {
    const sendData = {
      service_type: 'soopi',
      list_send_type: 'client',
      list_view_type: 'icon',
    };
    if (isDisplay && !iframeVisible && location.pathname === ROUTE.INDEX) {
      LogApi.sendListViewLog(sendData);
    }
  }, [isDisplay, iframeVisible, location.pathname]);

  useEffect(() => {
    if (previewOpen) {
      const {broadType, broadUrl, streamerName, data} = previewBroadInfo;

      if (!broadType || !broadUrl || !streamerName || !data) {
        return;
      }

      openSoopPreviewModal(streamerName, broadType, broadUrl, data);
    }
  }, [previewOpen]);

  const {size} = useFloatingSize(soopiTicket.icon);

  if (
    location.pathname !== ROUTE.INDEX ||
    !isDisplay ||
    soopiTicket.token === ''
  ) {
    return null;
  }

  return (
    <FloatingPortal target={'soopi_wrap'}>
      <AnimatePresence>
        {!iframeVisible && (
          <>
            <motion.div
              className={styles.soopiBtn}
              data-size={size}
              style={{
                cursor: isDragging ? 'grabbing' : 'grab',
              }}
              initial={{
                opacity: 0,
                scale: 0.8,
                x: position.x,
                y: position.y,
              }}
              animate={{
                opacity: 1,
                scale: 1,
                x: position.x,
                y: position.y,
              }}
              exit={{
                opacity: 0,
                y: 200,
                scale: 0.5,
                transition: {duration: 1},
              }}
              drag
              dragElastic={0.5}
              dragTransition={{
                bounceStiffness: 300,
                bounceDamping: 50,
                power: 0.1,
              }}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onClick={handleOpenSoopi}>
              <InteractivePlayVideo
                src={decodeURI(
                  decodeURIComponent(soopiTicket?.persona?.streamerVideo),
                )}
              />
              {/* 251215 시즈널 테두리 api에서 내려올 때만 적용*/}
              {soopiTicket?.seasonalTheme ? (
                <div className={`${styles.customBorder}`}>
                  <img src={soopiTicket?.seasonalTheme} alt='' />
                </div>
              ) : (
                <SoopiBorder />
              )}
              <button
                className={styles.hoverButton}
                onClick={handleCloseSoopiBtn}
              />
            </motion.div>
          </>
        )}

        {iframeVisible && (
          <>
            <SoopiIframeContainer
              isResizing={isResizing}
              height={height}
              iframeUrl={iframeUrl}
              isLeft={isLeft}
              dragPointStyle={dragPointStyle}
              previewOpen={previewOpen}>
              <SoopiResizeScrollButton
                height={height}
                setHeight={setHeight}
                isResizing={isResizing}
                setIsResizing={setIsResizing}
              />
            </SoopiIframeContainer>
          </>
        )}
      </AnimatePresence>
    </FloatingPortal>
  );
};

export default SoopiComponent;
